import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, KeyRound, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Input, Skeleton } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

/*
 * Zwei-Faktor-Anmeldung (Authenticator-App): Schritt bei der Anmeldung, Einrichtung und Verwaltung im Profil.
 */

interface SetupData {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
}

const onError = (e: unknown) => toast.error(errorMessage(e));

function CodeInput({ id, value, onChange, autoFocus }: { id: string; value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  return (
    <Input
      id={id}
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      className="text-center font-mono text-2xl tracking-[0.5em]"
      autoFocus={autoFocus}
    />
  );
}

/** QR-Code und Geheimnis zum Abtippen */
function SetupInstructions({ data }: { data: SetupData }) {
  return (
    <div className="grid gap-3 text-sm">
      <p>
        1. Öffnen Sie eine Authenticator-App (z. B. Microsoft Authenticator, Google Authenticator, 1Password oder Bitwarden) und scannen Sie den QR-Code.
      </p>
      <img src={data.qrDataUrl} alt="QR-Code für die Authenticator-App" className="mx-auto size-44 rounded-md border bg-white p-1" />
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer">QR-Code lässt sich nicht scannen?</summary>
        <p className="mt-1">Schlüssel manuell eingeben (zeitbasiert, 6 Ziffern):</p>
        <code className="mt-1 block break-all rounded bg-muted p-2 font-mono text-foreground">{data.secret.match(/.{1,4}/g)?.join(' ')}</code>
      </details>
      <p>2. Geben Sie den 6-stelligen Code aus der App ein.</p>
    </div>
  );
}

/** Wiederherstellungscodes einmalig anzeigen */
export function RecoveryCodesView({ codes, onDone, doneLabel = 'Weiter' }: { codes: string[]; onDone: () => void; doneLabel?: string }) {
  const [saved, setSaved] = useState(false);
  const text = codes.join('\n');
  return (
    <div className="grid gap-3 text-sm">
      <Alert>
        Bewahren Sie diese Wiederherstellungscodes sicher auf (z. B. ausgedruckt oder im Passwortmanager). Jeder Code funktioniert einmal, falls Sie keinen Zugriff auf die Authenticator-App haben. Sie werden nur jetzt angezeigt.
      </Alert>
      <ul className="grid grid-cols-2 gap-1 rounded-md border bg-muted/50 p-3 font-mono">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            navigator.clipboard
              .writeText(text)
              .then(() => toast.success('Kopiert'))
              .catch(() => toast.error('Kopieren nicht möglich'))
          }
        >
          <Copy /> Kopieren
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            const a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([`Wiederherstellungscodes Serviceportal\n\n${text}\n`], { type: 'text/plain' }));
            a.download = 'wiederherstellungscodes.txt';
            a.click();
            URL.revokeObjectURL(a.href);
          }}
        >
          <Download /> Als Datei speichern
        </Button>
      </div>
      <label className="flex items-center gap-2">
        <Checkbox checked={saved} onCheckedChange={(v) => setSaved(v === true)} /> Ich habe die Codes sicher gespeichert
      </label>
      <Button type="button" disabled={!saved} onClick={onDone}>
        {doneLabel}
      </Button>
    </div>
  );
}

/** Zweiter Schritt der Anmeldung: Code prüfen oder (Pflicht für Mitarbeitende) einrichten */
export function LoginSecondFactor({ mfa, challenge, onDone, onCancel }: { mfa: 'verify' | 'setup'; challenge: string; onDone: () => void; onCancel: () => void }) {
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const setup = useQuery({ queryKey: ['2fa-setup', challenge], queryFn: () => api<SetupData>('/auth/2fa/setup', { method: 'POST', body: { challenge } }), enabled: mfa === 'setup', retry: false, staleTime: Infinity });
  const verify = useMutation({
    mutationFn: () => api<{ recoveryCodesLeft: number }>('/auth/2fa/verify', { method: 'POST', body: recovery !== null ? { challenge, recoveryCode: recovery } : { challenge, code } }),
    onSuccess: (r) => {
      if (recovery !== null) toast.info(`Wiederherstellungscode verwendet – noch ${r.recoveryCodesLeft} übrig. Neue Codes erzeugen Sie im Profil.`);
      onDone();
    },
  });
  const confirm = useMutation({
    mutationFn: () => api<{ recoveryCodes: string[] }>('/auth/2fa/setup/confirm', { method: 'POST', body: { challenge, code } }),
    onSuccess: (r) => setCodes(r.recoveryCodes),
  });

  if (codes) return <RecoveryCodesView codes={codes} onDone={onDone} doneLabel="Zum Portal" />;
  const error = verify.error ?? confirm.error ?? setup.error;
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (mfa === 'verify') verify.mutate();
        else confirm.mutate();
      }}
    >
      {error && <Alert variant="destructive">{errorMessage(error)}</Alert>}
      {mfa === 'setup' && (
        <>
          <Alert>Für Mitarbeiterkonten ist die Zwei-Faktor-Anmeldung vorgeschrieben. Richten Sie sie jetzt einmalig ein.</Alert>
          {setup.data ? <SetupInstructions data={setup.data} /> : <Skeleton className="h-56" />}
        </>
      )}
      {recovery === null ? (
        <Field label={mfa === 'verify' ? 'Code aus der Authenticator-App' : 'Code (6 Ziffern)'} htmlFor="mfa-code">
          <CodeInput id="mfa-code" value={code} onChange={setCode} autoFocus />
        </Field>
      ) : (
        <Field label="Wiederherstellungscode" htmlFor="mfa-recovery">
          <Input id="mfa-recovery" value={recovery} onChange={(e) => setRecovery(e.target.value)} placeholder="xxxxx-xxxxx" className="font-mono" autoFocus autoComplete="off" />
        </Field>
      )}
      <Button type="submit" loading={verify.isPending || confirm.isPending} disabled={recovery === null ? code.length !== 6 : recovery.trim().length < 10}>
        <ShieldCheck /> {mfa === 'verify' ? 'Bestätigen' : 'Einrichten und anmelden'}
      </Button>
      <div className="flex justify-between text-sm">
        <button type="button" className="text-muted-foreground hover:text-foreground" onClick={onCancel}>
          Abbrechen
        </button>
        {mfa === 'verify' && (
          <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setRecovery(recovery === null ? '' : null)}>
            {recovery === null ? 'Wiederherstellungscode verwenden' : 'Code aus der App verwenden'}
          </button>
        )}
      </div>
    </form>
  );
}

/** Verwaltung im Profil */
export function TwoFactorCard() {
  const qc = useQueryClient();
  const key = ['auth', '2fa'];
  const { data } = useQuery({ queryKey: key, queryFn: () => api<{ enabled: boolean; enabledAt: string | null; required: boolean; recoveryCodesLeft: number }>('/auth/2fa') });
  const [setup, setSetup] = useState<SetupData | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'disable' | 'recovery' | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const enroll = useMutation({ mutationFn: () => api<SetupData>('/auth/2fa/enroll', { method: 'POST' }), onSuccess: setSetup, onError });
  const enable = useMutation({
    mutationFn: () => api<{ recoveryCodes: string[] }>('/auth/2fa/enable', { method: 'POST', body: { code } }),
    onSuccess: (r) => {
      setSetup(null);
      setCode('');
      setCodes(r.recoveryCodes);
      refresh();
    },
    onError,
  });
  const disable = useMutation({
    mutationFn: () => api('/auth/2fa/disable', { method: 'POST', body: { password, code } }),
    onSuccess: () => {
      toast.success('Zwei-Faktor-Anmeldung abgeschaltet');
      setMode(null);
      setCode('');
      setPassword('');
      refresh();
    },
    onError,
  });
  const regenerate = useMutation({
    mutationFn: () => api<{ recoveryCodes: string[] }>('/auth/2fa/recovery-codes', { method: 'POST', body: { code } }),
    onSuccess: (r) => {
      setMode(null);
      setCode('');
      setCodes(r.recoveryCodes);
      refresh();
    },
    onError,
  });
  if (!data) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="size-4" /> Zwei-Faktor-Anmeldung
          {data.enabled ? <Badge variant="success">aktiv</Badge> : <Badge variant="outline">aus</Badge>}
        </CardTitle>
        <CardDescription>
          {data.required
            ? 'Für Mitarbeiterkonten vorgeschrieben: Bei der Anmeldung mit Passwort ist zusätzlich ein Code aus der Authenticator-App nötig. Bei „Mit Microsoft anmelden“ gilt die Mehrfaktor-Anmeldung von Microsoft.'
            : 'Empfohlen: Bei der Anmeldung ist zusätzlich ein Code aus einer Authenticator-App nötig.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm">
        {codes ? (
          <RecoveryCodesView codes={codes} onDone={() => setCodes(null)} doneLabel="Fertig" />
        ) : !data.enabled ? (
          setup ? (
            <form
              className="grid max-w-sm gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                enable.mutate();
              }}
            >
              <SetupInstructions data={setup} />
              <CodeInput id="mfa-enable" value={code} onChange={setCode} autoFocus />
              <Button type="submit" loading={enable.isPending} disabled={code.length !== 6}>
                Aktivieren
              </Button>
            </form>
          ) : (
            <div>
              <Button onClick={() => enroll.mutate()} loading={enroll.isPending}>
                <ShieldCheck /> Einrichten
              </Button>
            </div>
          )
        ) : (
          <>
            <p className="text-muted-foreground">
              Aktiv seit {formatDateTime(data.enabledAt)} · {data.recoveryCodesLeft} Wiederherstellungscodes übrig
            </p>
            {data.recoveryCodesLeft <= 3 && <Alert variant="destructive">Nur noch wenige Wiederherstellungscodes – bitte neue erzeugen.</Alert>}
            {mode === null ? (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setMode('recovery')}>
                  Neue Wiederherstellungscodes
                </Button>
                {!data.required && (
                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setMode('disable')}>
                    Abschalten
                  </Button>
                )}
              </div>
            ) : (
              <form
                className="grid max-w-sm gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (mode === 'disable') disable.mutate();
                  else regenerate.mutate();
                }}
              >
                {mode === 'disable' && (
                  <Field label="Passwort" htmlFor="mfa-pw">
                    <Input id="mfa-pw" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                  </Field>
                )}
                <Field label="Aktueller Code aus der App" htmlFor="mfa-current">
                  <CodeInput id="mfa-current" value={code} onChange={setCode} />
                </Field>
                <div className="flex gap-2">
                  <Button type="submit" variant={mode === 'disable' ? 'destructive' : 'default'} loading={disable.isPending || regenerate.isPending} disabled={code.length !== 6 || (mode === 'disable' && !password)}>
                    {mode === 'disable' ? 'Abschalten' : 'Neue Codes erzeugen'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setMode(null)}>
                    Abbrechen
                  </Button>
                </div>
              </form>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
